import moment from "moment";
import { useEffect, useState } from "react";
import { FiCheck, FiEye, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import SummaryApi from "../common";
import StarRating from "../components/StarRating";
import { request } from "../helpers/request";

const AllReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("pending");
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedReview, setSelectedReview] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchReviews();
  }, [filterStatus, currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const response = await request({
        url: `${SummaryApi.getAllReviews.url}?status=${filterStatus}&page=${currentPage}&limit=20`,
        method: SummaryApi.getAllReviews.method,
      });

      const data = await response.json();

      if (data.success) {
        setReviews(data.data || []);
        setPagination(data.pagination);
      } else {
        toast.error(data.message || "Failed to fetch reviews");
      }
    } catch (error) {
      toast.error("Failed to fetch reviews");
    } finally {
      setLoading(false);
    }
  };

  const handleModerate = async (reviewId, status) => {
    try {
      const response = await request({
        url: SummaryApi.moderateReview.url,
        method: SummaryApi.moderateReview.method,
        data: { reviewId, status },
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`Review ${status} successfully`);
        fetchReviews();
        setSelectedReview(null);
      } else {
        toast.error(data.message || "Failed to moderate review");
      }
    } catch (error) {
      toast.error("Failed to moderate review");
    }
  };

  const StatusBadge = ({ status }) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-700",
      approved: "bg-green-100 text-green-700",
      rejected: "bg-red-100 text-red-700",
    };

    return (
      <span
        className={`px-2 py-1 text-xs rounded-full ${colors[status] || colors.pending}`}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">
          Moderate Reviews
        </h1>

        {/* Filter Tabs */}
        <div className="flex gap-2">
          {["pending", "approved", "rejected"].map((status) => (
            <button
              key={status}
              onClick={() => {
                setFilterStatus(status);
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded-md font-medium transition-colors ${
                filterStatus === status
                  ? "bg-red-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block bg-white rounded-3xl shadow-premium border border-surface-border overflow-hidden">
        <table className="min-w-full divide-y divide-surface-border">
          <thead className="bg-surface-muted/50">
            <tr>
              <th className="px-8 py-6 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">
                Product
              </th>
              <th className="px-8 py-6 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">
                User
              </th>
              <th className="px-8 py-6 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">
                Rating
              </th>
              <th className="px-8 py-6 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">
                Review
              </th>
              <th className="px-8 py-6 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">
                Date
              </th>
              <th className="px-8 py-6 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">
                Status
              </th>
              <th className="px-8 py-6 text-left text-[10px] font-black uppercase tracking-widest text-gray-400 text-center">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-surface-border">
            {reviews.map((review) => (
              <tr
                key={review._id}
                className="group hover:bg-surface-muted/30 transition-colors"
              >
                <td className="px-8 py-6">
                  <div
                    className="font-bold text-primary truncate max-w-[200px]"
                    title={review.productId?.productName}
                  >
                    {review.productId?.productName || "Unknown Product"}
                  </div>
                </td>
                <td className="px-8 py-6">
                  <div className="flex flex-col">
                    <span className="font-bold text-primary">
                      {review.userId?.name || "Anonymous"}
                    </span>
                    <span className="text-[10px] text-gray-400 font-bold tracking-tight">
                      {review.userId?.email || ""}
                    </span>
                  </div>
                </td>
                <td className="px-8 py-6">
                  <StarRating rating={review.rating} size="sm" showNumber />
                </td>
                <td className="px-8 py-6 max-w-xs">
                  <div className="text-sm font-bold text-primary mb-0.5 truncate">
                    {review.title}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {review.comment}
                  </div>
                </td>
                <td className="px-8 py-6">
                  <span className="text-sm font-medium text-gray-500">
                    {moment(review.createdAt).format("MMM D, YYYY")}
                  </span>
                </td>
                <td className="px-8 py-6">
                  <StatusBadge status={review.status} />
                </td>
                <td className="px-8 py-6">
                  <div className="flex justify-center gap-2">
                    <button
                      onClick={() => setSelectedReview(review)}
                      className="w-10 h-10 flex items-center justify-center rounded-xl bg-surface-muted text-gray-400 hover:bg-primary hover:text-white transition-all shadow-sm"
                      title="View Details"
                    >
                      <FiEye size={18} />
                    </button>
                    {review.status !== "approved" && (
                      <button
                        onClick={() => handleModerate(review._id, "approved")}
                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-green-50 text-green-600 hover:bg-green-600 hover:text-white transition-all shadow-sm"
                        title="Approve"
                      >
                        <FiCheck size={18} />
                      </button>
                    )}
                    {review.status !== "rejected" && (
                      <button
                        onClick={() => handleModerate(review._id, "rejected")}
                        className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 text-red-600 hover:bg-red-600 hover:text-white transition-all shadow-sm"
                        title="Reject"
                      >
                        <FiX size={18} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {reviews.map((review) => (
          <div
            key={review._id}
            className="bg-white rounded-3xl border border-surface-border p-6 shadow-sm"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1 min-w-0">
                <h3
                  className="text-sm font-black text-primary truncate capitalize mb-1"
                  title={review.productId?.productName}
                >
                  {review.productId?.productName || "Unknown Product"}
                </h3>
                <div className="flex items-center gap-2">
                  <StarRating rating={review.rating} size="sm" />
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    {moment(review.createdAt).format("MMM D")}
                  </span>
                </div>
              </div>
              <StatusBadge status={review.status} />
            </div>

            <div className="bg-surface-muted/50 p-4 rounded-2xl mb-6">
              <p className="font-bold text-primary text-sm mb-1">
                {review.title}
              </p>
              <p className="text-xs text-gray-600 line-clamp-3">
                {review.comment}
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center text-primary font-black text-xs border border-surface-border">
                  {review.userId?.name?.charAt(0) || "A"}
                </div>
                <div className="flex flex-col">
                  <span className="text-xs font-bold text-primary">
                    {review.userId?.name || "Anonymous"}
                  </span>
                  {review.verified && (
                    <span className="text-[8px] font-black text-green-600 uppercase tracking-widest">
                      Verified Purchase
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedReview(review)}
                  className="w-10 h-10 flex items-center justify-center rounded-xl bg-surface-muted text-primary active:scale-95 transition-all"
                >
                  <FiEye />
                </button>
                {review.status === "pending" && (
                  <>
                    <button
                      onClick={() => handleModerate(review._id, "approved")}
                      className="w-10 h-10 flex items-center justify-center rounded-xl bg-green-50 text-green-600 active:scale-95 transition-all"
                    >
                      <FiCheck />
                    </button>
                    <button
                      onClick={() => handleModerate(review._id, "rejected")}
                      className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 text-red-600 active:scale-95 transition-all"
                    >
                      <FiX />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {reviews.length === 0 && (
        <div className="h-64 flex flex-col items-center justify-center bg-white rounded-3xl border border-surface-border text-center p-8 shadow-premium">
          <p className="text-sm font-bold text-gray-500">
            No {filterStatus} reviews to show
          </p>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={!pagination.hasPrevPage}
            className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-gray-700">
            Page {pagination.currentPage} of {pagination.totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((prev) => prev + 1)}
            disabled={!pagination.hasNextPage}
            className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}

      {/* Review Detail Modal */}
      {selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold">Review Details</h2>
                <button
                  onClick={() => setSelectedReview(null)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <FiX size={20} />
                </button>
              </div>

              <div className="space-y-4">
                {/* Product Info */}
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Product
                  </label>
                  <p className="text-gray-800 font-medium">
                    {selectedReview.productId?.productName || "Unknown Product"}
                  </p>
                </div>

                {/* User Info */}
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    User
                  </label>
                  <p className="text-gray-800">
                    {selectedReview.userId?.name || "Anonymous"}
                  </p>
                  <p className="text-sm text-gray-600">
                    {selectedReview.userId?.email || ""}
                  </p>
                </div>

                {/* Rating */}
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Rating
                  </label>
                  <div className="mt-1">
                    <StarRating
                      rating={selectedReview.rating}
                      size="lg"
                      showNumber
                    />
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Title
                  </label>
                  <p className="text-gray-800 font-medium">
                    {selectedReview.title}
                  </p>
                </div>

                {/* Comment */}
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    Review
                  </label>
                  <p className="text-gray-800 whitespace-pre-line">
                    {selectedReview.comment}
                  </p>
                </div>

                {/* Images */}
                {selectedReview.images && selectedReview.images.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-600 mb-2 block">
                      Images
                    </label>
                    <div className="flex gap-2 flex-wrap">
                      {selectedReview.images.map((img, index) => (
                        <img
                          key={index}
                          src={img}
                          alt={`Review ${index + 1}`}
                          className="w-32 h-32 object-cover rounded-md border border-gray-300 cursor-pointer hover:opacity-75"
                          onClick={() => window.open(img, "_blank")}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Meta Info */}
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      Status
                    </label>
                    <div className="mt-1">
                      <StatusBadge status={selectedReview.status} />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      Helpful Votes
                    </label>
                    <p className="text-gray-800">
                      {selectedReview.helpful || 0}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      Verified Purchase
                    </label>
                    <p className="text-gray-800">
                      {selectedReview.verified ? "Yes" : "No"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      Created
                    </label>
                    <p className="text-gray-800">
                      {moment(selectedReview.createdAt).format(
                        "DD MMM YYYY, h:mm A",
                      )}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  {selectedReview.status !== "approved" && (
                    <button
                      onClick={() =>
                        handleModerate(selectedReview._id, "approved")
                      }
                      className="flex-1 px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2"
                    >
                      <FiCheck /> Approve
                    </button>
                  )}
                  {selectedReview.status !== "rejected" && (
                    <button
                      onClick={() =>
                        handleModerate(selectedReview._id, "rejected")
                      }
                      className="flex-1 px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium flex items-center justify-center gap-2"
                    >
                      <FiX /> Reject
                    </button>
                  )}
                  {selectedReview.status !== "pending" && (
                    <button
                      onClick={() =>
                        handleModerate(selectedReview._id, "pending")
                      }
                      className="flex-1 px-6 py-3 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors font-medium"
                    >
                      Set Pending
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllReviews;
